import { format, parseISO } from 'date-fns';
import { router } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { formatCentsToDollars } from '@fhs/menu/src/utils';
import { IconAdd, IconChevronRight, IconRemove, IconStarRateFill, Text, tokens } from '@fhs/ui';
import { TransactionReferenceIdType } from '@fhs-legacy/frontend/src/enums/loyalty';
import { ITransactionFragment } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { TransactionChannel } from '@fhs-legacy/frontend/src/pages/loyalty/transactions/types';
import { Nullable } from '@rbi-ctg/frontend';

interface Props {
  transaction: ITransactionFragment;
}

const getTransactionMessage = (channel: string) => {
  switch (channel) {
    case TransactionChannel.Support: {
      return 'Support Points';
    }

    case TransactionChannel.PointsExpiration: {
      return 'Points Expired';
    }

    case TransactionChannel.Campaign: {
      return 'Campaign';
    }

    case TransactionChannel.App:
    case TransactionChannel.Web: {
      return 'Digital Order';
    }

    case TransactionChannel.Restaurant: {
      return 'Restaurant Order';
    }

    case TransactionChannel.InitialPointBalance: {
      return 'Initial Point Balance';
    }

    case TransactionChannel.Quest: {
      return 'Challenge';
    }

    default: {
      return null;
    }
  }
};

function PointsDisplay({
  points,
  isNegative,
  textLabel,
  color,
}: {
  points?: Nullable<number>;
  isNegative?: boolean;
  textLabel: string;
  color?: string;
}) {
  if (!points) {
    return null;
  }
  const textStyle = isNegative ? styles.usedPoints : styles.earnedPoints;
  const symbol = isNegative ? '-' : '+';
  const textColor = color ?? (isNegative ? tokens.colors.$error : tokens.colors.$houseDark);
  return (
    <View style={styles.pointsRow}>
      <IconStarRateFill
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        aria-hidden
        color={tokens.colors.$houseYellow}
        style={styles.starIcon}
      />
      <Text.Ui size="sm" style={[textStyle, { color: textColor }]}>
        {symbol}
        <Text.Ui size="sm" style={[textStyle, { color: textColor }]} weight="semibold">
          {points.toLocaleString()}
        </Text.Ui>{' '}
        {textLabel}
      </Text.Ui>
    </View>
  );
}

export function TransactionItem({ transaction }: Props) {
  const pointsEarned = transaction.pointsEarned ?? 0;
  const bonusPointsEarned = transaction.bonusPointsEarned ?? 0;
  const pointsDeducted = transaction.pointsDeducted ?? 0;
  const pointsUsed = transaction.pointsUsed ?? 0;
  const isPositiveTransaction = pointsEarned + bonusPointsEarned - pointsDeducted - pointsUsed > 0;

  const isOneLinePointsDisplay =
    [pointsEarned, bonusPointsEarned, pointsDeducted, pointsUsed].filter(points => points > 0)
      .length === 1;
  const transactionType = getTransactionMessage(transaction.channel);

  const transactionAmount = transaction.transactionDetails?.payments?.[0]?.amount || 0;

  const formattedDate = format(parseISO(transaction.createdAt), 'MMM d, yyyy - h:mm a');

  const orderReference = transaction?.additionalReferenceIds?.find(
    ({ type }) => type === TransactionReferenceIdType.RBIOrderId
  );
  const rbiOrderId = orderReference?.id;
  const isDisabled = !rbiOrderId;

  const rowAccessibilityLabel = useMemo(() => {
    const parts: string[] = [formattedDate];
    if (transactionType) {
      parts.push(transactionType);
    }
    if (pointsEarned > 0) {
      parts.push(`${pointsEarned.toLocaleString()} points earned`);
    }
    if (bonusPointsEarned > 0) {
      parts.push(`${bonusPointsEarned.toLocaleString()} bonus points`);
    }
    if (pointsUsed > 0) {
      parts.push(`${pointsUsed.toLocaleString()} points used`);
    }
    if (pointsDeducted > 0) {
      parts.push(`${pointsDeducted.toLocaleString()} points expired`);
    }
    parts.push(`Order total ${formatCentsToDollars(transactionAmount)}`);
    if (!isDisabled) {
      parts.push('Opens order receipt');
    } else {
      parts.push('Receipt not available');
    }
    return parts.join('. ');
  }, [
    formattedDate,
    transactionType,
    pointsEarned,
    bonusPointsEarned,
    pointsUsed,
    pointsDeducted,
    transactionAmount,
    isDisabled,
  ]);

  const onPress = () => {
    rbiOrderId && router.dismissTo(`/receipt/${rbiOrderId}`);
  };

  return (
    <Pressable
      accessibilityLabel={rowAccessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled }}
      disabled={isDisabled}
      onPress={onPress}
      style={styles.container}
    >
      <View accessible={false} style={styles.content}>
        <View style={styles.transactionInfo}>
          <View style={styles.iconContainer}>
            {isPositiveTransaction ? (
              <IconAdd
                accessibilityElementsHidden
                aria-hidden
                importantForAccessibility="no-hide-descendants"
                color={tokens.colors.$success}
                size={20}
              />
            ) : (
              <IconRemove
                accessibilityElementsHidden
                aria-hidden
                importantForAccessibility="no-hide-descendants"
                color={tokens.colors.$error}
                size={20}
              />
            )}
          </View>

          <View style={styles.detailsContainer}>
            <Text.Ui size="md" style={styles.dateText}>
              {formattedDate}
            </Text.Ui>
            <View
              style={[styles.pointsContent, isOneLinePointsDisplay && styles.oneLinePointsContent]}
            >
              {transactionType && (
                <Text.Ui size="sm" style={styles.transactionType}>
                  {transactionType}
                </Text.Ui>
              )}
              <PointsDisplay points={transaction.pointsEarned} textLabel="Earned" />
              <PointsDisplay
                points={transaction.bonusPointsEarned}
                textLabel="Bonus"
                color={tokens.colors.$success}
              />
              <PointsDisplay points={transaction.pointsUsed} isNegative textLabel="Used" />
              <PointsDisplay points={transaction.pointsDeducted} isNegative textLabel="Expired" />
            </View>
          </View>
        </View>

        <View style={styles.amountContainer}>
          <Text.Ui
            size="sm"
            weight="semibold"
            style={{ color: isDisabled ? tokens.colors.$disabledText : tokens.colors.$houseDark }}
          >
            {formatCentsToDollars(transactionAmount)}
          </Text.Ui>
          <IconChevronRight
            accessibilityElementsHidden
            aria-hidden
            importantForAccessibility="no-hide-descendants"
            style={styles.chevronIcon}
            size={20}
            color={isDisabled ? tokens.colors.$blackOpacity10 : tokens.colors.$houseDark}
          />
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    flexDirection: 'row',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  transactionInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 100,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  detailsContainer: {
    flex: 1,
    marginLeft: 12,
  },
  dateText: {
    color: tokens.colors.$black,
    marginBottom: 4,
  },
  descriptionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  transactionType: {
    color: tokens.colors.$blackOpacity75,
  },
  pointsContent: {
    flex: 1,
    gap: 4,
  },
  oneLinePointsContent: {
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  pointsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  starIcon: {
    marginRight: 2,
  },

  earnedPoints: {
    color: tokens.colors.$black,
  },
  usedPoints: {
    color: tokens.colors.$error,
  },
  amountContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  chevronIcon: {
    marginLeft: 4,
  },
});
